import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { authApi, ApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

export function ForgotPasswordPage() {
  const [email, setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone]     = useState(false);
  const [error, setError]   = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.forgotPassword(email);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4"
           style={{ background: '#1e3a5f' }}>
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <img src="/logo.png?v=4" alt="VantixMT"
                 className="w-40 sm:w-56 object-contain drop-shadow-xl" />
          </div>
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-12 text-center">
            <div className="flex justify-center mb-5">
              <div className="w-16 h-16 rounded-full bg-brand-50 flex items-center justify-center">
                <Mail size={32} className="text-brand-600" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Check your inbox</h2>
            <p className="text-slate-500 text-sm mb-1">If <span className="font-semibold text-slate-800">{email}</span> is registered,</p>
            <p className="text-slate-500 text-sm mb-8">we've sent a password reset link. It expires in 1 hour.</p>
            <Link to="/login" className="text-brand-600 hover:underline text-sm font-medium">
              Back to sign in
            </Link>
          </div>
          <p className="text-center text-xs text-brand-200/80 mt-4">
            Didn't receive it? Check your spam folder.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
         style={{ background: '#1e3a5f' }}>
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.png?v=4" alt="VantixMT"
               className="w-40 sm:w-56 object-contain drop-shadow-xl" />
        </div>
        <div className="bg-white rounded-2xl shadow-2xl px-8 py-10">
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold text-slate-900">Forgot password?</h1>
            <p className="text-sm text-slate-500 mt-1">
              Enter your work email and we'll send a reset link.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Input
              label="Work Email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoFocus
            />

            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</p>
            )}

            <Button type="submit" className="w-full mt-2" disabled={loading}>
              {loading ? 'Sending…' : 'Send reset link'}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-5">
            <Link to="/login" className="text-brand-600 hover:underline font-medium">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
